import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 20;

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize }) => {
    const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const to = Math.min(currentPage * pageSize, totalItems);

    const getPages = () => {
        const delta = 2, pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) pages.push(i);
        }
        const items = []; let prev = null;
        for (const page of pages) {
            if (prev && page - prev > 1) items.push('...');
            items.push(page); prev = page;
        }
        return items;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 bg-white">
            <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-700">{from}–{to}</span> of <span className="font-semibold text-gray-700">{totalItems}</span> results
                {totalPages > 1 && <span className="text-gray-400 ml-1">· Page {currentPage} of {totalPages}</span>}
            </p>
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        ‹ Prev
                    </button>
                    {getPages().map((item, i) =>
                        item === '...'
                            ? <span key={`e${i}`} className="w-8 text-center text-xs text-gray-400">…</span>
                            : <button key={item} onClick={() => onPageChange(item)}
                                className={`w-8 h-8 text-xs font-semibold rounded-md transition-colors ${currentPage === item ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                {item}
                            </button>
                    )}
                    <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        Next ›
                    </button>
                </div>
            )}
        </div>
    );
};

const CustomEventAdminApprove = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [adminRole, setAdminRole] = useState('');

    const [activeTab, setActiveTab] = useState('conference'); // 'conference' | 'workshop'

    // Checkbox toggles loading state
    const [approvalLoading, setApprovalLoading] = useState(null);

    // File upload
    const fileInputRef = useRef(null);
    const [uploadLoading, setUploadLoading] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'approved', 'pending'
    const [currentPage, setCurrentPage] = useState(1);

    // Image Modal
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        const storedAuth = localStorage.getItem('kapconAdminAuth');
        if (storedAuth) {
            try {
                const parsed = JSON.parse(storedAuth);
                setCredentials({ username: parsed.username, password: parsed.password });
                setAdminRole(parsed.role || '');
                setAuthenticated(true);
            } catch (e) {
                console.error(e);
            }
        }
    }, []);

    useEffect(() => {
        if (authenticated) {
            fetchParticipants();
        }
    }, [authenticated, activeTab]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, filterStatus, activeTab]);

    const handleLoginChange = (e) => {
        setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const fetchParticipants = async () => {
        try {
            setLoading(true);
            const endpoint = activeTab === 'conference' ? '/api/custom-events/admin/participants' : '/api/custom-events/admin/workshop/participants';
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                setParticipants(data.data || []);
            } else {
                if (res.status === 401) {
                    setAuthenticated(false);
                    localStorage.removeItem('kapconAdminAuth');
                }
                setMessage(data.message || 'Error fetching data');
            }
        } catch (error) {
            console.error(error);
            setMessage('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                const authData = { username: credentials.username, password: credentials.password, role: data.role };
                localStorage.setItem('kapconAdminAuth', JSON.stringify(authData));
                setAdminRole(data.role || '');
                setAuthenticated(true);
            } else {
                setMessage(data.message || 'Invalid credentials');
                localStorage.removeItem('kapconAdminAuth');
            }
        } catch (error) {
            setMessage('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('kapconAdminAuth');
        setAuthenticated(false);
        setAdminRole('');
    };

    // 1. Single Toggle
    const handleToggleApproval = async (id, currentStatus) => {
        const newStatus = !currentStatus;
        if (!window.confirm(`Mark this participant as ${newStatus ? 'APPROVED' : 'UNAPPROVED'}?`)) return;

        setApprovalLoading(id);
        try {
            const endpoint = activeTab === 'conference'
                ? `/api/custom-events/admin/participant/${id}/approve`
                : `/api/custom-events/admin/workshop/participant/${id}/approve`;
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, isApproved: newStatus })
            });
            const data = await res.json();
            if (data.success) {
                setParticipants(prev => prev.map(p => p._id === id ? { ...p, isApproved: newStatus, paymentStatus: newStatus ? 'completed' : 'pending' } : p));
            } else {
                alert('Update failed: ' + data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Error updating approval status');
        } finally {
            setApprovalLoading(null);
        }
    };

    // 2. Download Excel Template
    const exportApprovalTemplate = () => {
        const dataForExcel = participants.map(p => ({
            'Registration ID': p.registrationId,
            'Name': p.fullName,
            'Transaction ID': p.transactionId || '',
            'Amount Due': Number(p.paymentAmount || p.amountAmount || 0),
            'Currently Approved': p.isApproved ? 'Yes' : 'No',
            'Approved (Yes/No)': p.isApproved ? 'Yes' : 'No' // This is the column that admins will edit
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);

        // Make columns a bit wider
        const colWidths = [
            { wch: 15 }, // Reg ID
            { wch: 30 }, // Name
            { wch: 25 }, // Trans ID
            { wch: 15 }, // Amount
            { wch: 20 }, // Currently
            { wch: 20 }, // Approved editable
        ];
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Approval');
        XLSX.writeFile(workbook, `KAPCON_Bulk_Approval_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // 3. Upload and Parse Excel
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadLoading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const parsedData = XLSX.utils.sheet_to_json(sheet);

            if (parsedData.length === 0) {
                throw new Error("Excel file is empty.");
            }

            // Extract only Registration ID and Approved Status
            const updates = parsedData.map(row => {
                const regId = row['Registration ID'] || row['Reg ID'] || row['RegistrationId'];
                const rawApproved = String(row['Approved (Yes/No)'] || row['Approved'] || row['Currently Approved']).trim().toLowerCase();
                const isApproved = rawApproved === 'yes' || rawApproved === 'true' || rawApproved === '1' || rawApproved === 'y';

                return {
                    registrationId: regId,
                    isApproved
                };
            }).filter(u => u.registrationId); // filter out empty rows

            if (updates.length === 0) {
                throw new Error("Could not find a 'Registration ID' column in the uploaded file.");
            }

            // Send to Backend
            const endpoint = activeTab === 'conference'
                ? `/api/custom-events/admin/participants/bulk-approve`
                : `/api/custom-events/admin/workshop/participants/bulk-approve`;
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, updates })
            });

            const result = await res.json();

            if (result.success) {
                alert(`Bulk Approval Successful!\n${result.message}`);
                if (result.errors && result.errors.length > 0) {
                    console.warn("Some rows had errors:", result.errors);
                    alert("Completed with some skipped rows. Check browser console for details.");
                }
                fetchParticipants(); // Refresh list automatically
            } else {
                alert('Bulk upload failed: ' + result.message);
            }

        } catch (err) {
            console.error(err);
            alert(`Error reading Excel file: ${err.message}`);
        } finally {
            setUploadLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Render logic
    if (!authenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Approval Dashboard Login
                    </h2>
                </div>
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <form className="space-y-6" onSubmit={handleLogin}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Username</label>
                                <div className="mt-1">
                                    <input name="username" type="text" required value={credentials.username} onChange={handleLoginChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <div className="mt-1">
                                    <input name="password" type="password" required value={credentials.password} onChange={handleLoginChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>
                            {message && <div className="text-red-600 text-sm">{message}</div>}
                            <div>
                                <button type="submit" disabled={loading} className="w-full flex justify-center py-2 border border-transparent rounded-md text-white bg-blue-600 hover:bg-blue-700">
                                    {loading ? 'Logging in...' : 'Log in'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Role check
    if (adminRole !== 'full') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
                    <p className="text-gray-600 mb-6">You need full admin privileges to access the Approval Dashboard.</p>
                    <button onClick={handleLogout} className="px-4 py-2 border bg-gray-100 rounded text-gray-700">Go Back / Logout</button>
                </div>
            </div>
        )
    }

    // Filters
    let filteredList = [...participants];

    if (filterStatus === 'approved') filteredList = filteredList.filter(p => !!p.isApproved);
    if (filterStatus === 'pending') filteredList = filteredList.filter(p => !p.isApproved);

    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredList = filteredList.filter(p =>
            (p.registrationId || '').toLowerCase().includes(term) ||
            (p.transactionId || '').toLowerCase().includes(term) ||
            (p.fullName || '').toLowerCase().includes(term)
        );
    }

    // Sort logic (newest first)
    filteredList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);
    const pagedList = filteredList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const pendingCount = participants.filter(p => !p.isApproved).length;

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 border-l-4 border-purple-500 pl-3">Manual Approvals</h1>
                        <p className="mt-1 text-sm text-gray-500 pl-4">{pendingCount} pending manual payment verifications for {activeTab}.</p>
                    </div>
                    {/* TABS */}
                    <div className="flex bg-white rounded-lg p-1 border shadow-sm self-stretch md:self-auto">
                        <button onClick={() => setActiveTab('conference')} className={`flex-1 md:flex-none px-5 py-2 text-sm font-bold rounded-md transition-colors ${activeTab === 'conference' ? 'bg-purple-500 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Conference</button>
                        <button onClick={() => setActiveTab('workshop')} className={`flex-1 md:flex-none px-5 py-2 text-sm font-bold rounded-md transition-colors ${activeTab === 'workshop' ? 'bg-purple-500 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Workshop</button>
                    </div>
                    <div className="flex flex-wrap gap-3 items-center">
                        <button onClick={fetchParticipants} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 text-sm font-medium">
                            {loading ? '↻...' : '↻ Refresh'}
                        </button>
                        <a href="/event/jss/admin/data" className="text-indigo-600 hover:text-indigo-900 text-sm font-semibold mx-2">Data View &rarr;</a>
                        <button onClick={handleLogout} className="px-3 py-1.5 border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 rounded text-sm font-semibold">Logout</button>
                    </div>
                </div>

                {/* Bulk Actions Card */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-purple-100 mb-6 flex flex-col lg:flex-row gap-6 items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-1">Bulk Excel Approvals</h3>
                        <p className="text-sm text-gray-500 max-w-xl">Download the list, mark the 'Approved' column as 'Yes', and re-upload the file to securely approve participants in bulk without opening individual rows.</p>
                    </div>
                    <div className="flex items-center gap-3 w-full lg:w-auto">
                        <button
                            onClick={exportApprovalTemplate}
                            className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm flex-1 whitespace-nowrap"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                            1. Download List
                        </button>

                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            className="hidden"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                        />
                        <button
                            onClick={() => fileInputRef.current.click()}
                            disabled={uploadLoading}
                            className="bg-blue-600 text-white border border-transparent hover:bg-blue-700 font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm flex-1 whitespace-nowrap disabled:opacity-75"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                            {uploadLoading ? 'Uploading...' : '2. Upload List'}
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex gap-4 flex-wrap">
                    <input
                        type="text"
                        placeholder="Search Reg ID, Transaction ID, Name..."
                        className="px-4 py-2 border rounded-md min-w-[300px] text-sm focus:ring-purple-500 focus:border-purple-500 flex-1 lg:flex-none"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="px-4 py-2 border rounded-md text-sm bg-gray-50 focus:ring-purple-500 focus:border-purple-500"
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                    >
                        <option value="all">View All Requires-Payment</option>
                        <option value="pending">View Pending Only</option>
                        <option value="approved">View Approved Only</option>
                    </select>
                </div>

                {/* Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-[#fcf8f5]">
                                <tr>
                                    <th className="px-5 py-4 text-left font-semibold text-gray-700 uppercase tracking-wide">Participant</th>
                                    <th className="px-5 py-4 text-left font-semibold text-gray-700 uppercase tracking-wide">Category & Fees</th>
                                    <th className="px-5 py-4 text-left font-semibold text-gray-700 uppercase tracking-wide min-w-[200px]">Transaction Details</th>
                                    <th className="px-5 py-4 text-center font-semibold text-gray-700 uppercase tracking-wide">Admin Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {pagedList.length === 0 ? (
                                    <tr><td colSpan="4" className="text-center py-6 text-gray-500">{searchTerm || filterStatus !== 'all' ? 'No records match your filters.' : 'No records found.'}</td></tr>
                                ) : (
                                    pagedList.map(p => (
                                        <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                                            {/* Participant Info */}
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="font-mono font-bold text-gray-900 mb-1">{p.registrationId}</div>
                                                <div className="font-semibold text-gray-800">{p.fullName}</div>
                                                <div className="text-gray-500 text-xs mt-0.5">{p.email}</div>
                                                <div className="text-gray-400 text-xs">{new Date(p.createdAt).toLocaleDateString()}</div>
                                                {p.registrationSource === 'bulk-admin' && (
                                                    <span className="inline-block mt-1.5 px-2 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-600 text-[10px] font-bold rounded-full uppercase tracking-wide">
                                                        ⬆ Bulk Upload (Admin)
                                                    </span>
                                                )}
                                            </td>

                                            {/* Category & Fee */}
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="inline-block bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-semibold mb-1">
                                                    {p.registrationCategory}
                                                </div>
                                                <div className="font-bold text-gray-900 text-lg">
                                                    ₹{Number(p.paymentAmount || p.amountAmount || 0)}
                                                </div>
                                            </td>

                                            {/* Transaction Details */}
                                            <td className="px-5 py-4">
                                                {p.transactionId ? (
                                                    <div className="mb-2">
                                                        <span className="text-xs text-gray-500 uppercase tracking-wider block font-semibold mb-0.5">Tran ID</span>
                                                        <span className="font-mono bg-gray-100 px-2 py-1 rounded text-gray-800 font-semibold">{p.transactionId}</span>
                                                    </div>
                                                ) : <span className="text-gray-400 text-xs italic block mb-2">No Trxn ID</span>}

                                                {p.transactionScreenshotUrl ? (
                                                    <button onClick={() => setPreviewImage(p.transactionScreenshotUrl)} type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline bg-transparent border-none cursor-pointer">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                                        View Receipt Image
                                                    </button>
                                                ) : <span className="text-gray-400 text-xs flex items-center gap-1"><span className="text-red-400">✗</span> No Receipt Uploaded</span>}
                                            </td>

                                            {/* Action Toggle */}
                                            <td className="px-5 py-4 text-center align-middle relative">
                                                <button
                                                    onClick={() => handleToggleApproval(p._id, p.isApproved)}
                                                    disabled={approvalLoading === p._id}
                                                    className={`
                                                        relative overflow-hidden px-6 py-2.5 rounded-xl font-bold text-sm shadow-sm transition-all
                                                        ${p.isApproved
                                                            ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-500 hover:bg-emerald-200'
                                                            : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-purple-500 hover:text-purple-700'}
                                                    `}
                                                >
                                                    {approvalLoading === p._id ? 'Updating...' : p.isApproved ? '✓ APPROVED' : 'APPROVE'}
                                                </button>
                                                {p.isApproved && (
                                                    <p className="text-[10px] text-emerald-600 font-semibold mt-2 uppercase tracking-wide">Payment Confirmed</p>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={filteredList.length}
                        pageSize={PAGE_SIZE}
                    />
                </div>

            </div>

            {/* Image Preview Modal */}
            {previewImage && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-75" onClick={() => setPreviewImage(null)}>
                    <div className="relative max-w-4xl w-full max-h-[90vh] p-2 bg-white rounded-lg shadow-2xl flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setPreviewImage(null)} className="absolute top-4 right-4 text-gray-800 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow hover:bg-gray-100 z-10 font-bold text-xl leading-none">&times;</button>
                        <img src={previewImage} alt="Receipt Preview" className="max-w-full max-h-[85vh] object-contain rounded" />
                    </div>
                </div>
            )}
        </div>
    );

};

export default CustomEventAdminApprove;
